/**
 * Brand logo compositor for live preview + clip recording.
 *
 * Cover-crops the AR canvas into a FIXED 9:16 frame, then draws a solid title
 * mark at the bottom. Black in Logo.png is knocked out once so we can paint
 * the red type opaque — no blend mode.
 *
 * The fixed size is the point. This canvas is both what the guest watches and
 * what MediaRecorder encodes, so pinning it to 9:16 is what makes the clip 9:16
 * on any camera. It used to size itself to the AR canvas and copy 1:1, which
 * meant the output inherited whatever shape the camera happened to hand over —
 * a web guest was framed 9:16 on screen by CSS `object-fit: cover` while the
 * file being recorded was the raw landscape 1280x720 the browser gave us, and
 * ResultScreen then cropped that a second time to display it. Three different
 * framings of the same moment, none of them agreeing.
 *
 * 720x1280 also happens to be exactly one panel of the transition matte, so the
 * fog maps onto the frame with no resampling at all.
 */

import { AR_CANVAS_HEIGHT, AR_CANVAS_WIDTH } from '../ar/types';
import { assets } from '../assets';
import { captureHealth } from '../lib/captureHealth';

/** Cap width so the wide title mark stays a lower-third, not a billboard. */
const LOGO_MAX_WIDTH_FRAC = 0.46;
/** Cap height so aspect stays true on any canvas size. */
const LOGO_MAX_HEIGHT_FRAC = 0.09;
/** Gap from the bottom edge, as a fraction of frame height. */
const LOGO_BOTTOM_PAD_FRAC = 0.035;
/** Near-black plate in Logo.png → transparent. */
const BLACK_LUMA_CUTOFF = 28;

export interface LogoCompositor {
  /** Canvas shown to the guest. */
  canvas: HTMLCanvasElement;
  /**
   * Canvas the recorder captures. The same element as `canvas` unless a
   * record size was given, in which case it is a downscaled copy drawn once
   * per tick — see createLogoCompositor.
   */
  recordCanvas: HTMLCanvasElement;
  /**
   * Extra layer drawn on top of everything, or null to clear it. Used for the
   * transition, which is otherwise a DOM element sitting above this canvas and
   * therefore absent from the recording. Sampling the same element the guest is
   * watching keeps preview and clip identical by construction.
   */
  setOverlay(source: HTMLVideoElement | HTMLCanvasElement | null): void;
  start(): void;
  stop(): void;
}

/**
 * Cover-fit: fill the frame, crop the overflow, centre what survives.
 *
 * Deliberately the same arithmetic as CSS `object-fit: cover`, because that is
 * how both the preview canvas and the DOM transition overlay are laid out.
 * Matching it here is what keeps the clip and the thing the guest was looking
 * at the same picture.
 */
function drawCover(
  ctx: CanvasRenderingContext2D,
  src: CanvasImageSource,
  sw: number,
  sh: number,
  w: number,
  h: number,
): void {
  const scale = Math.max(w / sw, h / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  try {
    ctx.drawImage(src, (w - dw) / 2, (h - dh) / 2, dw, dh);
  } catch {
    /* a video mid-seek or a canvas mid-resize can throw; skip the frame */
  }
}

/** Intrinsic size of whatever we have been handed, or null if not ready yet. */
function intrinsicSize(src: HTMLVideoElement | HTMLCanvasElement): [number, number] | null {
  if (src instanceof HTMLVideoElement) {
    // readyState < HAVE_CURRENT_DATA means there is no frame to sample.
    if (src.readyState < 2 || !src.videoWidth || !src.videoHeight) return null;
    return [src.videoWidth, src.videoHeight];
  }
  if (!src.width || !src.height) return null;
  return [src.width, src.height];
}

/**
 * The knocked-out mark, computed once per page.
 *
 * Both halves of this are expensive and neither belongs anywhere near the
 * trigger: Logo.png is ~630KB, and knockOutBlackPlate walks every one of its
 * 2172x724 pixels synchronously on the main thread. It used to run per
 * compositor — so once per capture — with the download landing seconds into the
 * live phase on a cold cache, which is roughly when a guest gets around to
 * cawing. Hoisting it here makes it once per page, and warmLogo lets the flows
 * pay for it on the title screen alongside the Camera Kit and MediaPipe warms.
 */
let logoPromise: Promise<CanvasImageSource | null> | null = null;

export function warmLogo(): Promise<CanvasImageSource | null> {
  if (logoPromise) return logoPromise;
  logoPromise = new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      try {
        resolve(knockOutBlackPlate(img));
      } catch {
        resolve(null);
      }
    };
    // A missing mark must not cost the guest the capture — the compositor just
    // draws the frame without it.
    img.onerror = () => resolve(null);
    img.src = assets.logo;
  });
  return logoPromise;
}

function knockOutBlackPlate(img: HTMLImageElement): HTMLCanvasElement {
  const cut = document.createElement('canvas');
  cut.width = img.naturalWidth;
  cut.height = img.naturalHeight;
  const c = cut.getContext('2d');
  if (!c) return cut;
  c.drawImage(img, 0, 0);
  const pixels = c.getImageData(0, 0, cut.width, cut.height);
  const d = pixels.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i] ?? 0;
    const g = d[i + 1] ?? 0;
    const b = d[i + 2] ?? 0;
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    if (luma < BLACK_LUMA_CUTOFF) d[i + 3] = 0;
  }
  c.putImageData(pixels, 0, 0);
  return cut;
}

/**
 * @param recordSize Record from a downscaled copy of this size instead of the
 *   720x1280 preview. The kiosk needs this: its VP8 encode is software on
 *   four A55 cores and at 720x1280 it managed ~6fps, ran seconds behind and
 *   dropped its queue when the recording stopped. One extra drawImage per
 *   tick, GPU to GPU, buys an encoder that can keep up.
 */
export function createLogoCompositor(
  source: HTMLCanvasElement,
  recordSize?: { width: number; height: number } | null,
): LogoCompositor {
  const canvas = document.createElement('canvas');
  // Fixed, and never resized to follow the source — see the note at the top.
  canvas.width = AR_CANVAS_WIDTH;
  canvas.height = AR_CANVAS_HEIGHT;
  canvas.setAttribute('data-logo-compositor', 'true');
  Object.assign(canvas.style, {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    position: 'absolute',
    inset: '0',
  });

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Logo compositor: 2d context unavailable');

  // The recorder's copy, when it is not simply the preview.
  const downscale =
    recordSize && (recordSize.width !== canvas.width || recordSize.height !== canvas.height);
  const recordCanvas = downscale ? document.createElement('canvas') : canvas;
  let rctx: CanvasRenderingContext2D | null = null;
  if (downscale) {
    recordCanvas.width = recordSize.width;
    recordCanvas.height = recordSize.height;
    recordCanvas.setAttribute('data-record-canvas', 'true');
    rctx = recordCanvas.getContext('2d', { alpha: false });
    if (!rctx) throw new Error('Logo compositor: 2d context unavailable for the record canvas');
  }

  let rafId = 0;
  let running = false;
  let logo: CanvasImageSource | null = null;
  let overlay: HTMLVideoElement | HTMLCanvasElement | null = null;

  // Resolves immediately on every capture after the first.
  void warmLogo().then((ready) => {
    logo = ready;
  });

  /** Light fade so solid red type stays readable — not a dark pillarbox. */
  function drawLogoGradient(w: number, h: number): void {
    const top = h * 0.78;
    const grad = ctx!.createLinearGradient(0, top, 0, h);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(0.45, 'rgba(0,0,0,0.25)');
    grad.addColorStop(1, 'rgba(0,0,0,0.4)');
    ctx!.fillStyle = grad;
    ctx!.fillRect(0, top, w, h - top);
  }

  function drawLogo(w: number, h: number): void {
    if (!logo) return;
    const nw = 'naturalWidth' in logo && logo.naturalWidth ? logo.naturalWidth : (logo as HTMLCanvasElement).width;
    const nh = 'naturalHeight' in logo && logo.naturalHeight ? logo.naturalHeight : (logo as HTMLCanvasElement).height;
    if (!nw || !nh) return;

    const aspect = nw / nh;
    let lw = w * LOGO_MAX_WIDTH_FRAC;
    let lh = lw / aspect;
    const maxH = h * LOGO_MAX_HEIGHT_FRAC;
    if (lh > maxH) {
      lh = maxH;
      lw = lh * aspect;
    }
    const x = (w - lw) / 2;
    const y = h - lh - h * LOGO_BOTTOM_PAD_FRAC;

    ctx!.drawImage(logo, x, y, lw, lh);
  }

  function drawOverlay(w: number, h: number): void {
    if (!overlay) return;
    const size = intrinsicSize(overlay);
    if (!size) return;
    drawCover(ctx!, overlay, size[0], size[1], w, h);
  }

  function tick(): void {
    if (!running) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx!.fillStyle = '#0b0c0a';
    ctx!.fillRect(0, 0, w, h);
    // Cover-crop rather than stretch: a landscape camera loses its sides, it
    // does not get squeezed into a portrait frame.
    if (source.width > 0 && source.height > 0) {
      drawCover(ctx!, source, source.width, source.height, w, h);
    }
    drawLogoGradient(w, h);
    drawLogo(w, h);
    // Last, so it covers the logo exactly as the DOM overlay does at z-30.
    // A full-frame wipe hiding the mark is what the guest sees, so it is what
    // the clip should show too.
    drawOverlay(w, h);
    if (rctx) rctx.drawImage(canvas, 0, 0, recordCanvas.width, recordCanvas.height);
    captureHealth.compositorTick();
    rafId = requestAnimationFrame(tick);
  }

  return {
    canvas,
    recordCanvas,
    setOverlay(next) {
      overlay = next;
    },
    start() {
      if (running) return;
      running = true;
      rafId = requestAnimationFrame(tick);
    },
    stop() {
      running = false;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      canvas.parentElement?.removeChild(canvas);
    },
  };
}
